import React, { FC } from 'react';
import Link from 'next/link';
import cn from 'classnames';
import { SvgIcon } from '@/components/ui/SvgIcon';
import { formatDate } from '@/lib/utils';
import { CmsEvent } from '@/lib/events';
import Styles from '@/app/events/events.module.scss';
import { IconNames } from '@/lib/icon-data';

export const renderEventItem = (ev: CmsEvent, index: number) => (
  <Link key={`${ev.slug}-${index}`} href={`/events/${ev.slug}`} className={Styles.eventItem}>
    <EventImage
      imageUrl={ev.imageUrl}
      icon={ev.category?.icon}
      alt={ev.title}
      size={70}
      imgClassName={Styles.eventImage}
      iconClassName={Styles.categoryIcon}
    />
    <div className={Styles.time} suppressHydrationWarning>
      {formatDate(ev.start, 'h:mm a')}
    </div>
    <div className={Styles.eventDetails}>
      <h3>{ev.title}</h3>
    </div>
  </Link>
);

export const renderDateItem = (grouped: Record<string, CmsEvent[]>) => {
  const DateItem = (dateKey: string) => (
    <li key={dateKey} className={Styles.eventRow}>
      <div className={Styles.dateWrapper}>
        <h3 suppressHydrationWarning>{formatDate(grouped[dateKey][0]?.start, 'EEEE, MMMM d')}</h3>
      </div>
      <div className={Styles.eventWrapper}>
        {grouped[dateKey].map(renderEventItem)}
      </div>
    </li>
  );

  (DateItem as unknown as { displayName?: string }).displayName = 'DateItem';
  return DateItem;
};

export const renderChip = (removeSelected: (name: string) => void) => {
  const fn = (name: string) => (
    <button key={name} className={Styles.chip} onClick={() => removeSelected(name)} aria-label={`Remove ${name}`}>
      {name} <span className={Styles.chipRemove}>×</span>
    </button>
  );
  (fn as unknown as { displayName?: string }).displayName = 'EventChip';
  return fn;
};

export const renderFilterCard = (toggleCategory: (name: string,) => void, selected: string[]) => {
  const fn = ({name, icon}: {name: string, icon: IconNames}) => {
    const isOn = selected.includes(name);
    return (
      <li key={name} className={cn(Styles.filterItem, { [Styles.active]: isOn })} onClick={() => toggleCategory(name)}>
        <span className={Styles.filterItemIcon}>
          <SvgIcon size={24} iconName={icon} />
        </span>
        {name}
      </li>
    );
  };
  (fn as unknown as { displayName?: string }).displayName = 'FilterCard';
  return fn;
};

type EventImageProps = {
  imageUrl?: string | null;
  icon?: IconNames;
  alt?: string;
  size?: number;
  imgClassName?: string;
  iconClassName?: string;
};

export const EventImage: FC<EventImageProps> = ({ imageUrl, icon, alt = '', size = 70, imgClassName, iconClassName }) => {
  if (imageUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    const img = <img src={imageUrl} alt={alt} className={imgClassName} />;
    return img;
  }

  if (!icon) {
    return null;
  }

  const iconEl = (
    <div className={iconClassName} aria-hidden>
      <SvgIcon iconName={icon} size={size} />
    </div>
  );

  return iconEl;
};

